import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  ProgressBar,
  Toast,
  ToastContainer,
  Nav,
  Tab
} from 'react-bootstrap';
import {
  FaPlay,
  FaStop,
  FaSync,
  FaComments,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaWifi,
  FaBan,
  FaStream,
  FaRobot,
  FaChartLine,
  FaProjectDiagram,
  FaTachometerAlt,
  FaUsers,
  FaServer,
  FaGlobe,
  FaShieldAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import axios from 'axios';

// Import new components
import S3EventStream from './S3EventStream';
import AgentChat from './AgentChat';
import WorkflowVisualizer from './WorkflowVisualizer';
import UsageAnalytics from './UsageAnalytics';
import S3ClaudeOrchestrator from './S3ClaudeOrchestrator';
import WhiteLabelManagement from './WhiteLabelManagement';
import RoleBasedAccessControl from './RoleBasedAccessControl';
import CompleteUserSetup from './CompleteUserSetup';
import EventManagement from './EventManagement';
import ContactsHub from './ContactsHub';

// Create a dedicated axios instance for AI Operations API
const aiOpsAPI = axios.create({
  baseURL: process.env.REACT_APP_AI_OPS_API || 'http://localhost:8081',
  withCredentials: true
});

// Add auth token from localStorage
aiOpsAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

interface AgentStatus {
  id: string;
  name: string;
  status: 'active' | 'offline' | 'error';
  health: number;
  lastSeen: string;
  taskCount: number;
  incidents: number;
}

interface MigrationProgress {
  total: number;
  completed: number;
  in_progress: number;
  pending: number;
  success_rate: string;
}

interface SystemMetrics {
  cost_reduction: string;
  response_time: string;
  uptime: string;
  events_processed: number;
}

interface Notification {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const AIOperationsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab } = useParams<{ tab?: string }>();
  const [agents, setAgents] = useState<AgentStatus[]>([]);
  const [migrationProgress, setMigrationProgress] = useState<MigrationProgress>({
    total: 100,
    completed: 45,
    in_progress: 12,
    pending: 43,
    success_rate: '92.5%'
  });
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics>({
    cost_reduction: '0%',
    response_time: '0 seconds',
    uptime: '0h 0m',
    events_processed: 0
  });
  const [eventStream, setEventStream] = useState<string[]>([]);
  const [commandDialog, setCommandDialog] = useState<{
    open: boolean;
    agentId: string;
    agentName: string;
  }>({
    open: false,
    agentId: '',
    agentName: ''
  });
  const [command, setCommand] = useState('');
  const [notification, setNotification] = useState<Notification>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [connected, setConnected] = useState(false);
  const [wsConnection, setWsConnection] = useState<WebSocket | null>(null);
  const [wsRetryCount, setWsRetryCount] = useState(0);
  const maxWsRetries = 3;

  // Determine active tab from URL parameter
  const getActiveTab = () => {
    if (!tab) return 'overview';
    
    // Map URL-friendly names to tab keys
    const tabMapping: Record<string, string> = {
      'agents': 'agents',
      's3-events': 's3-events',
      'workflows': 'workflows',
      'chat': 'chat',
      'analytics': 'usage-analytics',
      'orchestrator': 's3-orchestrator',
      'websites': 'white-label',
      'access-control': 'access-control',
      'user-setup': 'user-setup',
      'contacts': 'contacts',
      'events': 'events'
    };
    
    return tabMapping[tab] || 'overview';
  };

  // Handle tab changes by updating URL
  const handleTabSelect = (key: string | null) => {
    if (!key) return;
    
    if (key === 'overview') {
      navigate('/ai-operations');
    } else {
      navigate(`/ai-operations/${key}`);
    }
  };

  useEffect(() => {
    fetchInitialData();
    // Only try to connect WebSocket a few times
    if (wsRetryCount < maxWsRetries) {
      connectWebSocket();
    }
    
    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, []);

  const fetchInitialData = async () => {
    // Fetch each endpoint independently to avoid total failure
    const fetchWithFallback = async (endpoint: string, setter: (data: any) => void, fallbackData: any) => {
      try {
        const response = await aiOpsAPI.get(endpoint);
        if (response.data.success) {
          setter(response.data.data);
        }
      } catch (error) {
        console.warn(`Failed to fetch ${endpoint}, using fallback data`);
        if (fallbackData) setter(fallbackData);
      }
    };

    // Try to fetch data independently
    await Promise.all([
      fetchWithFallback('/api/agents/status', setAgents, null),
      fetchWithFallback('/api/migration/progress', setMigrationProgress, null),
      fetchWithFallback('/api/system/metrics', setSystemMetrics, null)
    ]);
    
    // Show notification only if all failed
    if (!agents && !migrationProgress && !systemMetrics) {
      setNotification({
        open: true,
        message: 'AI Operations service not available. Some features may be limited.',
        severity: 'info'
      });
      
      // Set demo data
      setAgents([
        {
          id: 'agent-1',
          name: 'DocumentProcessor-Alpha',
          status: 'active',
          health: 98,
          lastSeen: new Date().toISOString(),
          taskCount: 24,
          incidents: 0
        },
        {
          id: 'agent-2',
          name: 'ComplianceChecker-Beta',
          status: 'active',
          health: 95,
          lastSeen: new Date().toISOString(),
          taskCount: 18,
          incidents: 1
        },
        {
          id: 'agent-3',
          name: 'NotificationHandler-Gamma',
          status: 'offline',
          health: 0,
          lastSeen: new Date(Date.now() - 900000).toISOString(),
          taskCount: 0,
          incidents: 3
        }
      ]);
    }
  };

  const connectWebSocket = () => {
    try {
      const ws = new WebSocket(process.env.REACT_APP_AI_OPS_WS || 'ws://localhost:8082');
      
      ws.onopen = () => {
        setConnected(true);
        console.log('Connected to AI Operations WebSocket');
        // Subscribe to updates
        ws.send(JSON.stringify({ type: 'subscribe' }));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onclose = () => {
        setConnected(false);
        console.log('WebSocket disconnected');
        // Attempt to reconnect after 5 seconds if under retry limit
        if (wsRetryCount < maxWsRetries) {
          setWsRetryCount(prev => prev + 1);
          console.log(`WebSocket reconnect attempt ${wsRetryCount + 1}/${maxWsRetries}...`);
          setTimeout(connectWebSocket, 5000);
        } else {
          console.log('WebSocket max retries reached, stopping reconnection attempts');
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnected(false);
      };
      
      setWsConnection(ws);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setConnected(false);
    }
  };

  const handleWebSocketMessage = (data: any) => {
    const timestamp = new Date(data.timestamp).toLocaleTimeString();
    
    switch (data.type) {
      case 'agent_health_update':
        setAgents(prev => 
          prev.map(agent => 
            agent.id === data.agent_id 
              ? { ...agent, ...data.data, lastSeen: data.timestamp }
              : agent
          )
        );
        setEventStream(prev => [
          `${timestamp}: Agent ${data.data.name} status changed to ${data.data.status}`,
          ...prev
        ].slice(0, 20));
        break;
        
      case 'migration_progress':
        if (data.data.completed !== undefined) {
          setMigrationProgress(prev => ({ ...prev, ...data.data }));
        }
        if (data.data.latest_event) {
          setEventStream(prev => [
            `${timestamp}: ${data.data.latest_event}`,
            ...prev
          ].slice(0, 20));
        }
        break;
        
      case 'live_update':
        setEventStream(prev => [
          `${timestamp}: ${data.channel} - ${data.data.message || 'Event received'}`,
          ...prev
        ].slice(0, 20));
        break;
        
      case 'command_sent':
        setEventStream(prev => [
          `${timestamp}: Command sent to ${data.agent_id}`,
          ...prev
        ].slice(0, 20));
        break;
        
      case 'initial_data':
        // Handle initial data load
        if (data.agents) setAgents(data.agents);
        if (data.migrationProgress) setMigrationProgress(data.migrationProgress);
        if (data.systemMetrics) setSystemMetrics(data.systemMetrics);
        break;
        
      default:
        if (data.data?.message) {
          setEventStream(prev => [
            `${timestamp}: ${data.data.message}`,
            ...prev
          ].slice(0, 20));
        }
    }
  };

  const handleAgentAction = async (agentId: string, action: 'start' | 'stop' | 'restart') => {
    try {
      const response = await aiOpsAPI.post(`/api/agents/${agentId}/${action}`);
      if (response.data.success) {
        setNotification({
          open: true,
          message: `Agent ${action} command sent successfully`,
          severity: 'success'
        });
        fetchInitialData(); // Refresh data
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Failed to ${action} agent`,
        severity: 'error'
      });
    }
  };

  const openCommandDialog = (agentId: string, agentName: string) => {
    setCommandDialog({
      open: true,
      agentId,
      agentName
    });
    setCommand('');
  };

  const handleSendCommand = async () => {
    if (!command.trim()) return;

    try {
      const response = await aiOpsAPI.post('/api/agents/command', {
        agentId: commandDialog.agentId,
        command: command
      });

      if (response.data.success) {
        setEventStream(prev => [
          `${new Date().toLocaleTimeString()}: Command sent to ${commandDialog.agentName}: ${command}`,
          ...prev
        ].slice(0, 20));
        
        setNotification({
          open: true,
          message: 'Command sent successfully',
          severity: 'success'
        });
        
        setCommandDialog({ open: false, agentId: '', agentName: '' });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to send command',
        severity: 'error'
      });
    }
  };

  const getHealthColor = (health: number) => {
    if (health >= 90) return 'success';
    if (health >= 70) return 'warning';
    return 'danger';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <FaCheckCircle className="text-success" />;
      case 'offline': return <FaExclamationTriangle className="text-warning" />;
      case 'error': return <FaTimesCircle className="text-danger" />;
      default: return null;
    }
  };

  return (
    <Container fluid className="p-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>🚀 KairosIQ AI Workforce Management Platform</h2>
        <div className="d-flex align-items-center gap-3">
          <Badge bg="info" className="px-3 py-2">
            <FaUsers className="me-2" />
            {agents.length} AI Workers
          </Badge>
          {connected ? (
            <Badge bg="success" className="px-3 py-2">
              <FaWifi className="me-2" />
              Live Operations
            </Badge>
          ) : (
            <Badge bg="danger" className="px-3 py-2">
              <FaBan className="me-2" />
              Disconnected
            </Badge>
          )}
        </div>
      </div>

      {/* System Metrics */}
      <Row className="mb-3">
        <Col xs={12} sm={6} md={3}>
          <Card>
            <Card.Body>
              <h6>Cost Reduction</h6>
              <h3 className="text-primary">{systemMetrics.cost_reduction}</h3>
              <small className="text-muted">vs. traditional infrastructure</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card>
            <Card.Body>
              <h6>Response Time</h6>
              <h3 className="text-info">{systemMetrics.response_time}</h3>
              <small className="text-muted">average processing time</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card>
            <Card.Body>
              <h6>System Uptime</h6>
              <h3 className="text-success">{systemMetrics.uptime}</h3>
              <small className="text-muted">continuous operation</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card>
            <Card.Body>
              <h6>Events Processed</h6>
              <h3 className="text-warning">{systemMetrics.events_processed.toLocaleString()}</h3>
              <Badge bg="warning">S3 Events</Badge>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabbed Interface */}
      <Tab.Container activeKey={getActiveTab()} onSelect={handleTabSelect}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="overview">
              <FaTachometerAlt className="me-2" />
              Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="agents">
              <FaUsers className="me-2" />
              AI Workforce
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="s3-events">
              <FaStream className="me-2" />
              S3 Event Stream
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="workflows">
              <FaProjectDiagram className="me-2" />
              Workflow Visualization
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="chat">
              <FaRobot className="me-2" />
              Agent Chat
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="usage-analytics">
              <FaChartLine className="me-2" />
              Usage Analytics
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="s3-orchestrator">
              <FaServer className="me-2" />
              S3 Claude Orchestrator
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="white-label">
              <FaGlobe className="me-2" />
              Websites
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="access-control">
              <FaShieldAlt className="me-2" />
              Access Control
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="user-setup">
              <FaUsers className="me-2" />
              User Setup
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="contacts">
              <FaUsers className="me-2" />
              Contacts
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="events">
              <FaCalendarAlt className="me-2" />
              Events
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Overview Tab */}
          <Tab.Pane eventKey="overview">
            {/* Main Content */}
            <Row className="mb-3">
              <Col xs={12} md={8}>
                <Card>
                  <Card.Header>
                    <h5>Migration Progress</h5>
                  </Card.Header>
            <Card.Body>
              <ProgressBar className="mb-3">
                <ProgressBar
                  variant="success"
                  now={(migrationProgress.completed / migrationProgress.total) * 100}
                  label={`${migrationProgress.completed}`}
                />
                <ProgressBar
                  variant="warning"
                  now={(migrationProgress.in_progress / migrationProgress.total) * 100}
                  label={`${migrationProgress.in_progress}`}
                />
              </ProgressBar>
              
              <Row>
                <Col xs={3}>
                  <h6 className="text-success">{migrationProgress.completed}</h6>
                  <small>Completed</small>
                </Col>
                <Col xs={3}>
                  <h6 className="text-warning">{migrationProgress.in_progress}</h6>
                  <small>In Progress</small>
                </Col>
                <Col xs={3}>
                  <h6>{migrationProgress.pending}</h6>
                  <small>Pending</small>
                </Col>
                <Col xs={3}>
                  <h6 className="text-primary">{migrationProgress.success_rate}</h6>
                  <small>Success Rate</small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={12} md={4}>
          <Card>
            <Card.Header>
              <h5>Recent Events</h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {eventStream.length === 0 ? (
                <p className="text-muted">No recent events</p>
              ) : (
                eventStream.map((event, index) => (
                  <div key={index} className="small mb-1">
                    {event}
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

          </Tab.Pane>

          {/* Agent Workforce Tab */}
          <Tab.Pane eventKey="agents">
            <Card>
              <Card.Header className="bg-dark text-white">
                <h5 className="mb-0">AI Workforce Status</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Status</th>
                      <th>Health</th>
                      <th>Last Seen</th>
                      <th>Active Tasks</th>
                      <th>Incidents</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
              {agents.map((agent) => (
                <tr key={agent.id}>
                  <td>{agent.name}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {getStatusIcon(agent.status)}
                      <Badge 
                        bg={agent.status === 'active' ? 'success' : 
                            agent.status === 'offline' ? 'warning' : 'danger'}
                      >
                        {agent.status}
                      </Badge>
                    </div>
                  </td>
                  <td>
                    <ProgressBar 
                      now={agent.health} 
                      variant={getHealthColor(agent.health)}
                      label={`${agent.health}%`}
                      style={{ minWidth: '100px' }}
                    />
                  </td>
                  <td>{new Date(agent.lastSeen).toLocaleString()}</td>
                  <td>
                    <Badge bg="primary">{agent.taskCount}</Badge>
                  </td>
                  <td>
                    <Badge bg={agent.incidents > 0 ? 'danger' : 'success'}>
                      {agent.incidents}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleAgentAction(agent.id, 'start')}
                        disabled={agent.status === 'active'}
                      >
                        <FaPlay />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleAgentAction(agent.id, 'stop')}
                        disabled={agent.status === 'offline'}
                      >
                        <FaStop />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleAgentAction(agent.id, 'restart')}
                      >
                        <FaSync />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => openCommandDialog(agent.id, agent.name)}
                        disabled={agent.status === 'error' || !connected}
                      >
                        <FaComments />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* S3 Event Stream Tab */}
          <Tab.Pane eventKey="s3-events">
            <Row>
              <Col xs={12}>
                <S3EventStream wsConnection={wsConnection} />
              </Col>
            </Row>
          </Tab.Pane>

          {/* Workflow Visualization Tab */}
          <Tab.Pane eventKey="workflows">
            <WorkflowVisualizer wsConnection={wsConnection} />
          </Tab.Pane>

          {/* Agent Chat Tab */}
          <Tab.Pane eventKey="chat">
            <Row>
              <Col xs={12}>
                <AgentChat aiOpsAPI={aiOpsAPI} />
              </Col>
            </Row>
          </Tab.Pane>

          {/* Usage Analytics Tab */}
          <Tab.Pane eventKey="usage-analytics">
            <UsageAnalytics />
          </Tab.Pane>

          {/* S3 Claude Orchestrator Tab */}
          <Tab.Pane eventKey="s3-orchestrator">
            <S3ClaudeOrchestrator wsConnection={wsConnection} />
          </Tab.Pane>

          {/* White-Label Management Tab */}
          <Tab.Pane eventKey="white-label">
            <WhiteLabelManagement />
          </Tab.Pane>

          {/* Role-Based Access Control Tab */}
          <Tab.Pane eventKey="access-control">
            <RoleBasedAccessControl />
          </Tab.Pane>

          {/* Complete User Setup Tab */}
          <Tab.Pane eventKey="user-setup">
            <CompleteUserSetup />
          </Tab.Pane>
          <Tab.Pane eventKey="contacts">
            <ContactsHub />
          </Tab.Pane>
          <Tab.Pane eventKey="events">
            <EventManagement />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Command Dialog */}

      {/* Notification Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={notification.open}
          onClose={() => setNotification({ ...notification, open: false })}
          delay={3000}
          autohide
          bg={notification.severity === 'error' ? 'danger' : notification.severity}
        >
          <Toast.Body className="text-white">
            {notification.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default AIOperationsDashboard;